import { BackendErrorList, isAxiosError, isBackendErrorList } from '@eih-netbroker/common';
import { AxiosError } from 'axios';
import {
  CloseReason,
  closeSnackbar,
  enqueueSnackbar,
  SnackbarProvider as NSSnackbarProvider,
  VariantType,
} from 'notistack';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { NBSnackbar } from '../shared/components/snackbar/Snackbar';
import { Config } from '../shared/config/config';
import { useLogger } from '../shared/utils/useLogger';
import { useLocale } from './NbLocalizationContext';

export interface SnackbarOptionsBase {
  title?: string;
  persist?: boolean;
  onClose?: (reason: CloseReason) => void;
  preventDuplicate?: boolean;
}

export interface SnackbarOptionsError extends SnackbarOptionsBase {
  error?: unknown;
}

export type CloseSnackbarFn = () => void;

export type SnackbarContextProps =
  | undefined
  | {
      openSnackbar: (message: string, options?: SnackbarOptionsBase, sev?: VariantType) => CloseSnackbarFn;
      showError: (msgOrErr: string | unknown, options?: SnackbarOptionsError) => CloseSnackbarFn;
      showWarning: (message: string, options?: SnackbarOptionsBase) => CloseSnackbarFn;
      showSuccess: (message: string, options?: SnackbarOptionsBase) => CloseSnackbarFn;
      showInfo: (message: string, options?: SnackbarOptionsBase) => CloseSnackbarFn;
      closeAllSnackbars: () => void;
    };

const SnackbarContext = createContext<SnackbarContextProps>(undefined);

declare module 'notistack' {
  interface VariantOverrides {
    success: {
      title?: string;
    };
    error: {
      title?: string;
    };
    info: {
      title?: string;
    };
    warning: {
      title?: string;
    };
  }
}

export const SnackbarProvider = ({ children }: { children: React.ReactNode }) => {
  const logger = useLogger('SnackbarProvider');
  const locale = useLocale();
  const snackbarProviderRef = useRef<any | null>(null);

  const openSnackbar = useCallback(
    (message: string, options?: SnackbarOptionsBase, sev: VariantType = 'success'): CloseSnackbarFn => {
      const snackBar = enqueueSnackbar({
        message,
        variant: sev,
        title: options?.title,
        persist: options?.persist,
        onClose: (_, reason) => {
          options?.onClose?.(reason);
        },
        preventDuplicate: options?.preventDuplicate,
      });
      return () => {
        closeSnackbar(snackBar);
      };
    },
    [],
  );

  const showError = useCallback(
    (msgOrErr: string | unknown, options?: SnackbarOptionsError): CloseSnackbarFn => {
      const { error, persist, title, preventDuplicate } = options ?? {};
      const err = !!error ? error : (msgOrErr as unknown);
      const axiosError = err as AxiosError;
      const msg = typeof msgOrErr === 'string' ? `${msgOrErr}` : '';
      if (isAxiosError(axiosError) && isBackendErrorList(axiosError.response?.data)) {
        const message = (axiosError.response?.data as BackendErrorList).errors.map(e => e.errorMessage).join('\n');
        return openSnackbar(
          message,
          { title: locale.translation.general.toast.errorTitle, persist, preventDuplicate },
          'error',
        );
      } else if (axiosError.isAxiosError) {
        return openSnackbar(
          locale.translation.general.error.unhandledExceptionNetwork,
          { title: locale.translation.general.toast.errorTitle, persist, preventDuplicate },
          'error',
        );
      } else if (err && typeof (err as any).message === 'string') {
        const message = `${locale.translateWithParams(locale.translation.general.error.unhandledException, {
          message: (err as any).message,
        })}`;
        return openSnackbar(
          message,
          { title: title ?? locale.translation.general.toast.errorTitle, persist, preventDuplicate },
          'error',
        );
      } else {
        return openSnackbar(
          msg || locale.translation.general.error.serverError,
          { title: title ?? locale.translation.general.toast.errorTitle, persist, preventDuplicate },
          'error',
        );
      }
    },
    [locale, openSnackbar],
  );

  const showWarning = useCallback(
    (message: string, options?: SnackbarOptionsBase): CloseSnackbarFn => {
      return openSnackbar(
        message,
        { ...options, title: options?.title ?? locale.translation.general.toast.warningTitle },
        'warning',
      );
    },
    [locale, openSnackbar],
  );

  const showSuccess = useCallback(
    (message: string, options?: SnackbarOptionsBase): CloseSnackbarFn => {
      return openSnackbar(
        message,
        { ...options, title: options?.title ?? locale.translation.general.toast.successTitle },
        'success',
      );
    },
    [locale, openSnackbar],
  );

  const showInfo = useCallback(
    (message: string, options?: SnackbarOptionsBase): CloseSnackbarFn => {
      return openSnackbar(message, options, 'info');
    },
    [openSnackbar],
  );

  const closeAllSnackbars = useCallback(() => {
    snackbarProviderRef.current?.closeSnackbar();
  }, []);

  useEffect(() => {
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      if (isAxiosError(event.reason) && event.reason.code === 'ERR_NETWORK') {
        return showError(locale.translation.general.error.unhandledExceptionNetwork, { preventDuplicate: true });
      }

      logger.error('Unhandled promise rejection', event.reason);
    };
  }, [showError, locale, logger]);

  return (
    <SnackbarContext.Provider
      value={{ openSnackbar, showError, showWarning, showSuccess, showInfo, closeAllSnackbars }}
    >
      <NSSnackbarProvider
        maxSnack={5}
        autoHideDuration={Config.NetBrokerWeb.SnackbarTimeout}
        Components={{
          default: NBSnackbar,
          error: NBSnackbar,
          info: NBSnackbar,
          success: NBSnackbar,
          warning: NBSnackbar,
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ref={snackbarProviderRef}
      >
        {children}
      </NSSnackbarProvider>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error(`useSnackbar must be used within an SnackProvider`);
  }
  return context;
};
